import { RatingMethod } from '@shared/generated/graphql';
import { Quote } from '@shared/types/quote';
import {
  getMultiStopSupport,
  getMultiStopUnsupportedMessage,
  MultiStopSupport,
} from 'clerk_common/multiStopSupport';
import { getStopCount } from 'clerk_common/templates/freight_order/fieldExtractors/fieldExtractors';
import { Dictionary } from 'lodash';
import { createContext, useContext, useEffect } from 'react';

type RateWarningsContextType = {
  rateWarnings: Dictionary<string>;
  setRateWarning: (ratingMethod: RatingMethod, warning: string) => void;
  clearRateWarnings: () => void;
};

export const RateWarningsContext = createContext<RateWarningsContextType>({
  rateWarnings: {},
  setRateWarning: () => null,
  clearRateWarnings: () => null,
});

export function useRateWarningsContext() {
  const context = useContext(RateWarningsContext);
  if (!context) {
    throw new Error(
      'useRateWarningsContext must be used within a RateWarningsContext.Provider'
    );
  }
  return context;
}

export function useRateWarnContext(ratingMethod: RatingMethod, quote: Quote) {
  const { rateWarnings, setRateWarning } = useRateWarningsContext();
  useEffect(() => {
    if (!quote.extractedData) {
      return;
    }

    const stopCount = getStopCount(quote.extractedData);

    if (stopCount <= 2) {
      return;
    }

    const support = getMultiStopSupport(ratingMethod);
    const message = getMultiStopUnsupportedMessage(ratingMethod);

    if (support === MultiStopSupport.UNSUPPORTED_WARNING) {
      setRateWarning(ratingMethod, message);
    }
  }, [ratingMethod, quote.extractedData]);

  return {
    rateWarnings,
  };
}
